import axios from 'axios';
import config from '../config';
import { clearAuthTokens, setAuthTokens } from '../hooks/useIsLoggedIn';

const apiClient = axios.create({
  baseURL: config.API_BASE_URL,
  withCredentials: true,
});

let refreshPromise = null;

async function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${config.API_BASE_URL}/users/refreshToken`, null, {
        withCredentials: true,
        headers: {
          'X-Application-Id': config.APPLICATION_ID,
        },
      })
      .then((res) => {
        const { token } = res.data || {};

        if (!token) {
          throw new Error('Refresh response did not include a token');
        }

        // refreshToken is set by the API (HttpOnly); only persist rhaccesstoken client-side
        setAuthTokens(token);
        return token;
      })
      .catch((error) => {
        clearAuthTokens();
        throw error;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

apiClient.interceptors.request.use((requestConfig) => {
  if (!requestConfig.headers?.['X-Application-Id']) {
    requestConfig.headers = {
      ...requestConfig.headers,
      'X-Application-Id': config.APPLICATION_ID,
    };
  }

  return requestConfig;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      !originalRequest ||
      error.response?.status !== 401 ||
      originalRequest._retry ||
      originalRequest.url?.includes('/users/refreshToken')
    ) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      const token = await refreshAccessToken();

      originalRequest.headers = {
        ...originalRequest.headers,
        Authorization: `Bearer ${token}`,
      };

      return apiClient.request(originalRequest);
    } catch (refreshError) {
      return Promise.reject(refreshError);
    }
  },
);

export { refreshAccessToken };
export default apiClient;
